import React, { createContext, useContext, useState, useEffect } from 'react';

/**
 * Analytics Context
 *
 * Manages user consent for analytics tracking.
 * Required for compliance with Chilean Personal Data Protection Law (Ley 19.628).
 */

interface AnalyticsContextType {
  hasConsent: boolean;
  giveConsent: () => void;
  revokeConsent: () => void;
  isConsentDecided: boolean;
}

const AnalyticsContext = createContext<AnalyticsContextType | undefined>(undefined);

const CONSENT_KEY = 'analytics_consent';

interface AnalyticsProviderProps {
  children: React.ReactNode;
}

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({ children }) => {
  const [hasConsent, setHasConsent] = useState<boolean>(false);
  const [isConsentDecided, setIsConsentDecided] = useState<boolean>(false);

  useEffect(() => {
    // Check localStorage for previous consent decision
    const savedConsent = localStorage.getItem(CONSENT_KEY);
    if (savedConsent !== null) {
      setHasConsent(savedConsent === 'true');
      setIsConsentDecided(true);
    }
  }, []);

  const giveConsent = () => {
    localStorage.setItem(CONSENT_KEY, 'true');
    setHasConsent(true);
    setIsConsentDecided(true);
  };

  const revokeConsent = () => {
    localStorage.setItem(CONSENT_KEY, 'false');
    setHasConsent(false);
    setIsConsentDecided(true);

    // Clear all GA cookies
    document.cookie.split(';').forEach((c) => {
      const cookieName = c.trim();
      if (cookieName.startsWith('_ga')) {
        const cookieNameOnly = cookieName.split('=')[0];
        document.cookie = `${cookieNameOnly}=;expires=${new Date().toUTCString()};path=/`;
      }
    });
  };

  return (
    <AnalyticsContext.Provider
      value={{ hasConsent, giveConsent, revokeConsent, isConsentDecided }}
    >
      {children}
    </AnalyticsContext.Provider>
  );
};

/**
 * Hook to access analytics consent state
 *
 * @throws {Error} If used outside of AnalyticsProvider
 */
export const useAnalyticsConsent = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalyticsConsent must be used within AnalyticsProvider');
  }
  return context;
};
